// The device questions, asked here only: LAYOUT is width, INPUT is capability, plus the motion preference. False on
// the server, live from the hydration commit on. docs/notes/app/route-transition.md#detecting-a-phone

import { useLayoutEffect, useReducer, useSyncExternalStore } from 'react'

/**
 * Must match app.css's phone blocks. Range syntax, so no fractional width falls between. `screen`: an A4 page is
 * narrower than 48rem, and a printed report is the desktop layout, never the phone's stack.
 */
export const MOBILE_QUERY = 'screen and (width < 48rem)'

/** ANY pointer: a touch laptop swipes with its trackpad as the primary pointer. */
export const TOUCH_QUERY = '(any-pointer: coarse)'

/** The PRIMARY pointer, for what the user is holding right now: no haptic on a touch laptop's trackpad click. */
export const PRIMARY_TOUCH_QUERY = '(pointer: coarse)'

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

interface LiveValue {
  get: () => boolean
  subscribe: (listener: () => void) => () => void
}

/**
 * One MediaQueryList per query, created on first use, never at module scope, which SSR runs. `orFirstTouch`: a
 * touchscreen reporting no coarse pointer still announces itself with its first touch.
 */
function mediaValue(query: string, orFirstTouch = false): LiveValue {
  let list: MediaQueryList | null = null
  let touched = false
  const listeners = new Set<() => void>()
  const notify = () => {
    for (const listener of listeners) listener()
  }
  const onTouch = () => {
    touched = true
    window.removeEventListener('touchstart', onTouch, { capture: true })
    notify()
  }
  const ensure = (): MediaQueryList => {
    if (!list) {
      list = window.matchMedia(query)
      list.addEventListener('change', notify)
      if (orFirstTouch)
        window.addEventListener('touchstart', onTouch, {
          capture: true,
          passive: true,
        })
    }
    return list
  }
  return {
    get: () => touched || ensure().matches,
    subscribe: (listener) => {
      ensure()
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

const serverSnapshot = () => false
const bump = (n: number) => n + 1

/**
 * useSyncExternalStore corrects the server snapshot from a PASSIVE effect, a task after hydration, where a first tap
 * lands on `false`; a layout effect corrects it inside the hydration commit.
 */
function useLiveMedia(value: LiveValue): boolean {
  const rendered = useSyncExternalStore(value.subscribe, value.get, serverSnapshot)
  const [, rerender] = useReducer(bump, 0)
  useLayoutEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect -- hydration parity, see above
    if (rendered !== value.get()) rerender()
  }, [rendered, value])
  return rendered
}

const mobile = mediaValue(MOBILE_QUERY)
const touch = mediaValue(TOUCH_QUERY, true)
const primaryTouch = mediaValue(PRIMARY_TOUCH_QUERY)
const reducedMotion = mediaValue(REDUCED_MOTION_QUERY)

// False where there is nothing to ask: the server, and a test environment with no matchMedia.
const now = (value: LiveValue): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && value.get()

export const isMobileWidth = (): boolean => now(mobile)

/** For imperative code that follows the phone layout across a resize; client-only, returns the unsubscribe. */
export const subscribeMobileWidth = (listener: () => void): (() => void) => mobile.subscribe(listener)

export const canTouch = (): boolean => now(touch)

export const primaryPointerIsTouch = (): boolean => now(primaryTouch)

export const prefersReducedMotion = (): boolean => now(reducedMotion)

export function useIsMobile(): boolean {
  return useLiveMedia(mobile)
}

export function useCanTouch(): boolean {
  return useLiveMedia(touch)
}

export function usePrefersReducedMotion(): boolean {
  return useLiveMedia(reducedMotion)
}
